import { toUpliftRule, UpliftRule, UpliftRuleDto, UpliftRulesPage } from "domain/upliftRules";
import { apiGatewayService, ApiType } from "services/api/ApiGatewayService";
import { PricingRuleAction } from "services/asset-management/pricing-rules/PricingRuleService";
import { ParameterQuery } from "utils/commonFunctions";

const UPLIFT_RULES_PATH = "/api/v1/asset-management/uplift-rules";

const MAX_PAGE_SIZE = 500;

export interface UpliftRuleCreation {
    uuid?: string;
    headline: string;
    description?: string;
    value: number;
    action: PricingRuleAction;
}

interface UpliftRulesToCreate {
    rules: UpliftRuleCreation[];
}

interface UpliftRulesResponseDto {
    rules: UpliftRuleDto[];
    current_page: number;
    total_pages: number;
    total_rules: number;
}

class UpliftRulesService {
    public async fetchUpliftRules(
        page: number,
        size: number,
        searchQuery?: string,
        abortController?: AbortController
    ): Promise<UpliftRulesPage> {
        const query = new ParameterQuery()
            .add("page", page.toString())
            .add("size", size.toString())
            .add("search", searchQuery)
            .createQueryString();
        const response = await apiGatewayService.invokeApi<null, UpliftRulesResponseDto>(
            `${UPLIFT_RULES_PATH}${query}`,
            "GET",
            null,
            {
                abortSignal: abortController?.signal,
                apiType: ApiType.OLIVER,
            }
        );
        return {
            rules: response.rules.map(toUpliftRule),
            currentPage: response.current_page,
            totalPages: response.total_pages,
            totalRules: response.total_rules,
        };
    }

    public async fetchAllUpliftRules(abortController?: AbortController): Promise<UpliftRule[]> {
        const firstPage = await this.fetchUpliftRules(1, MAX_PAGE_SIZE, undefined, abortController);
        const remainingPageNumbers = Array.from(
            { length: Math.max(0, firstPage.totalPages - 1) },
            (_, index) => index + 2
        );
        const remainingPages = await Promise.all(
            remainingPageNumbers.map((page) => this.fetchUpliftRules(page, MAX_PAGE_SIZE, undefined, abortController))
        );
        return [firstPage, ...remainingPages].flatMap((page) => page.rules);
    }

    public async applyUpliftRules(request: UpliftRulesToCreate, abortController: AbortController) {
        return apiGatewayService.invokeApi(UPLIFT_RULES_PATH, "POST", request, {
            abortSignal: abortController?.signal,
            apiType: ApiType.OLIVER,
            emptyResponse: true,
        });
    }
}

export const upliftRulesService = new UpliftRulesService();
